import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useVisitor } from "@/lib/visitor";
import { intentById } from "@/lib/intents";
import { SmartLink } from "@/components/SmartLink";
import { useDevotionalsOn, keepLink } from "@/lib/display";

const StepCard = ({ step, i, color, onContact }) => {
  const inner = (
    <>
      <div className="relative h-48 overflow-hidden">
        <img src={step.img} alt={step.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1 text-xs font-bold" style={{ color }}>Step {i + 1}</span>
      </div>
      <div className="flex flex-1 items-start justify-between gap-4 p-6">
        <div>
          <h3 className="text-xl font-semibold text-slate-900">{step.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.text}</p>
        </div>
        <ArrowUpRight className="h-5 w-5 shrink-0 text-slate-300 transition-[transform,color] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#1D64B4]" />
      </div>
    </>
  );
  const cls = `group flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white text-left transition-shadow hover:shadow-xl ${i === 0 ? "md:row-span-2 md:[&>div:first-child]:h-72" : ""}`;
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
      className={i === 0 ? "md:row-span-2" : ""}>
      {step.action ? (
        <button onClick={() => onContact(step.action)} className={`${cls} h-full w-full`} data-testid={`next-step-card-${i}`}>{inner}</button>
      ) : (
        <SmartLink href={step.href} className={`${cls} h-full`} data-testid={`next-step-card-${i}`}>{inner}</SmartLink>
      )}
    </motion.div>
  );
};

export const NextSteps = () => {
  const { intent, openAction } = useVisitor();
  const on = useDevotionalsOn();
  const it = intentById(intent);
  if (!it) return null;
  return (
    <section id="next-steps" className="bg-[#F8FAFC] py-16 lg:py-24" data-testid="next-steps-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: it.color }}>Your next steps · {it.short}</p>
        <h2 className="mt-3 max-w-2xl text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">Here are a few ways we'd love to serve you.</h2>
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {it.steps.filter(keepLink(on)).map((s, i) => (
            <StepCard key={s.title} step={s} i={i} color={it.color} onContact={openAction} />
          ))}
        </div>
      </div>
    </section>
  );
};
